import React, { useState, useEffect, useMemo } from 'react';
import ProfileDropdown from './Auth/ProfileDropdown';
import { useAuth } from './Auth/AuthContext';
import { Link } from 'react-router-dom';
import featureFlags from '../config/featureFlags';
import useCart from '../hooks/useCart';
import { adaptCart } from '../api/adapters/cartAdapter';
import { adaptEntitlement } from '../api/adapters/entitlementAdapter';
import logo from '../assets/images/logo_white.png'; // Logo for dark background (not scrolled)
import logoGreen from '../assets/images/logo_green.png'; // Logo for white background (scrolled)

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const { user } = useAuth();
  const { data: rawCart } = useCart();
  const { priceSegment } = useMemo(() => adaptEntitlement(user), [user]);
  const cartVM = useMemo(() => adaptCart(rawCart, priceSegment), [rawCart, priceSegment]);
  const cartCount = cartVM.lines.reduce((sum, line) => sum + line.quantity, 0);

  // Brand color
  const accentColor = '#66c19d';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    {
      title: "Home",
      link: "/",
    },
    {
      title: "About",
      link: "/about",
    },
    {
      title: "Books",
      link: "/books",
    },
    {
      title: "Resources",
      link: "/resources",
      submenu: [
        { title: "Curriculum Overview", link: "/resources/curriculum" },
        { title: "Teaching Methodology", link: "/resources/pedagogy" },
        { title: "Assessment & Standards", link: "/resources/assessment" }
      ]
    },
    {
      title: "Philosophy",
      link: "/philosophy",
    },
    {
      title: "Contact",
      link: "/contact"
    },
    ...(featureFlags.ENABLE_SHOP ? [{ title: "Cart", link: "/cart" }] : []),
  ];

  return (
      <header 
        id="qodef-page-header" 
        className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md py-2' : 'bg-black bg-opacity-25 py-5'}`}
        role="banner"
      >
        <div className="container mx-auto px-4 flex justify-between items-center">
          {/* Logo - Changes based on scroll state */}
          <Link className="qodef-header-logo-link" to="/">
            <img 
              src={isScrolled ? logoGreen : logo} 
              alt="NLS Logo" 
              className={`transition-all duration-300 ${isScrolled ? 'h-10' : 'h-12'}`}
              style={{ maxWidth: '150px' }}
              width={160}
              height={48}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center" role="navigation" aria-label="Main Menu">
            <ul className="flex space-x-8">
              {menuItems.map((item, index) => (
                <li 
                  key={index} 
                  className={`group relative ${item.submenu ? 'menu-item-has-children' : ''}`}
                >
                  {item.submenu ? (
                    <button
                      type="button"
                      className={`font-medium flex items-center transition-colors duration-300 font-['Poppins',sans-serif] ${
                        isScrolled ? 'hover:opacity-80' : 'text-white hover:text-gray-200'
                      }`}
                      style={{ color: isScrolled ? accentColor : 'white' }}
                      aria-haspopup="menu"
                      aria-expanded={openSubmenu === item.title}
                      onMouseEnter={() => setOpenSubmenu(item.title)}
                      onFocus={() => setOpenSubmenu(item.title)}
                      onClick={() => setOpenSubmenu((current) => current === item.title ? null : item.title)}
                    >
                      <span>{item.title}</span>
                      <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </button>
                  ) : (
                    <Link 
                      to={item.link} 
                      className={`font-medium flex items-center transition-colors duration-300 font-['Poppins',sans-serif] ${
                        isScrolled 
                          ? 'hover:opacity-80'
                          : 'text-white hover:text-gray-200'
                      }`}
                      style={{
                        color: isScrolled ? accentColor : 'white'
                      }}
                    >
                      <span>{item.title}</span>
                    </Link>
                  )}
                  
                  {/* Dropdown Menu for Resources */}
                  {item.submenu && (
                    <div
                      className={`absolute left-0 mt-2 w-56 bg-white shadow-lg rounded-md transition-all duration-300 transform ${openSubmenu === item.title ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-2'}`}
                      onMouseLeave={() => setOpenSubmenu(null)}
                    >
                      <ul className="py-2">
                        {item.submenu.map((subItem, subIndex) => (
                          <li key={subIndex}>
                            <Link 
                              to={subItem.link} 
                              className="block px-4 py-2 transition-colors duration-300 hover:opacity-80"
                              style={{ color: accentColor }}
                            >
                              {subItem.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Auth + Cart actions (desktop only) */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Auth CTA */}
            {user ? (
              <ProfileDropdown user={user} isScrolled={isScrolled} />
            ) : (
              <Link
                to="/login"
                className="px-6 py-3 rounded-full font-medium transition-all duration-300 text-white hover:opacity-90"
                style={{ backgroundColor: accentColor }}
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Cart icon — always visible on all screen sizes */}
          {featureFlags.ENABLE_SHOP && (
            <Link
              to="/cart"
              aria-label={`Shopping cart, ${cartCount} items`}
              className={`relative flex items-center transition-colors duration-300 ${
                isScrolled ? 'text-primary-500' : 'text-white'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              {/* Badge — static 0 until SHOP-05 wires live cart count */}
              <span
                className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 rounded-full text-white text-[10px] font-semibold leading-none"
                style={{ backgroundColor: accentColor }}
                aria-hidden="true"
              >
                {cartCount}
              </span>
            </Link>
          )}

          {/* Mobile Menu Toggle */}
          <button 
            className={`lg:hidden focus:outline-none transition-colors duration-300`}
            style={{
              color: isScrolled ? accentColor : 'white'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7"></path>
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden bg-white shadow-lg transition-all duration-300 ${isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
          <nav className="container mx-auto px-4 py-4" role="navigation" aria-label="Mobile Menu">
            <ul className="space-y-2">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <Link 
                    to={item.link} 
                    className="block py-2 font-medium transition-colors duration-300"
                    style={{ color: accentColor }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                  {item.submenu && (
                    <ul className="pl-4 mt-2 space-y-2 border-l" style={{ borderColor: `${accentColor}40` }}>
                      {item.submenu.map((subItem, subIndex) => (
                        <li key={subIndex}>
                          <Link 
                            to={subItem.link} 
                            className="block py-1 transition-colors duration-300 hover:opacity-80"
                            style={{ color: accentColor }}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subItem.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
              <li className="pt-4">
                <Link 
                  to="/contact" 
                  className="block text-white text-center py-3 rounded-full font-medium transition-all hover:opacity-90"
                  style={{ 
                    backgroundColor: accentColor
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </li>
              {featureFlags.ENABLE_SHOP && (
                <li className="pt-2">
                  <Link
                    to="/cart"
                    className="flex items-center gap-2 py-2 font-medium transition-colors duration-300"
                    style={{ color: accentColor }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Cart ({cartCount})
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </header>
    );
};

export default Header;
